/**
 * AttributesList - Tree component for displaying and filtering attributes
 */
import { ReactElement, useState, useMemo, useEffect } from 'react'
import { connect } from 'react-redux'
import { Tree, Flex, Spin, Input } from 'antd'
import { fetchAttributes } from 'actions/search/attributes'
import {
  getAttributesTree,
  isFetchingAttributes,
} from 'selectors/search/attributes'
import { getSelectedAttributeCode } from 'selectors/search/ui'
import { setSelectedAttribute } from 'actions/search'
import type { AttributeTreeNode } from 'types/search/attributes'

interface AttributesListProps {
  treeData: AttributeTreeNode[]
  selectedAttributeCode: string | null
  isFetching: boolean
}

interface ReduxState {
  treeData: AttributeTreeNode[]
  selectedAttributeCode: string | null
  isFetching: boolean
}

const select = (): ReduxState => ({
  treeData: getAttributesTree(),
  selectedAttributeCode: getSelectedAttributeCode(),
  isFetching: isFetchingAttributes(),
})

/**
 * AttributesList - Displays hierarchical attribute tree with local filtering
 * Connected to Redux for autonomous data management
 */
const AttributesList = (props: AttributesListProps): ReactElement => {
  const { treeData, selectedAttributeCode, isFetching } = props

  const [attributeFilter, setAttributeFilter] = useState<string>('')
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([])

  // Fetch attributes on mount
  useEffect(() => {
    fetchAttributes()
  }, [])

  // Filter tree data based on search text
  const filteredTreeData = useMemo(() => {
    if (!attributeFilter.trim()) {
      return treeData
    }

    const filterText = attributeFilter.toLowerCase()
    const keysToExpand: React.Key[] = []

    // Recursive function to filter tree nodes
    const filterNode = (node: AttributeTreeNode): AttributeTreeNode | null => {
      const titleMatch = node.title.toLowerCase().includes(filterText)
      const pathMatch = node.path.toLowerCase().includes(filterText)

      // Filter children recursively
      const filteredChildren = node.children
        ?.map((child) => filterNode(child))
        .filter((child): child is AttributeTreeNode => child !== null)

      // Include node if it matches or if any child matches
      const hasChildren = filteredChildren && filteredChildren.length > 0
      if (titleMatch || pathMatch || hasChildren) {
        // If children match, expand this node
        if (hasChildren) {
          keysToExpand.push(node.key)
        }

        return {
          ...node,
          children: filteredChildren,
        }
      }

      return null
    }

    const filtered = treeData
      .map((node) => filterNode(node))
      .filter((node): node is AttributeTreeNode => node !== null)

    // Update expanded keys when filter changes
    setExpandedKeys(keysToExpand)

    return filtered
  }, [treeData, attributeFilter])

  const handleSelect = (selectedKeys: React.Key[]): void => {
    if (selectedKeys.length > 0) {
      setSelectedAttribute(selectedKeys[0] as string)
    } else {
      setSelectedAttribute(null)
    }
  }

  return (
    <Flex
      vertical
      style={{ padding: '8px', height: '100%', overflow: 'hidden' }}
    >
      <Input
        placeholder="Filter attributes..."
        allowClear
        value={attributeFilter}
        onChange={(e) => setAttributeFilter(e.target.value)}
        style={{ marginBottom: '8px' }}
      />
      <div style={{ flex: 1, overflow: 'auto' }}>
        {isFetching ? (
          <Flex justify="center" align="center" style={{ padding: 24 }}>
            <Spin tip="Loading attributes..." />
          </Flex>
        ) : (
          <Tree
            treeData={filteredTreeData}
            onSelect={handleSelect}
            selectedKeys={selectedAttributeCode ? [selectedAttributeCode] : []}
            expandedKeys={expandedKeys}
            onExpand={(keys) => setExpandedKeys(keys)}
            style={{
              whiteSpace: 'nowrap',
            }}
          />
        )}
      </div>
    </Flex>
  )
}

export default connect(select)(AttributesList)
